'use client'

import { useLocale, useTranslations } from 'next-intl'
import { Fragment, useMemo } from 'react'
import { NavigationDesktop } from '@/features/navigation/navigation-desktop'
import { INavigationDesktopItemProps } from '@/features/navigation/navigation-desktop-item'
import { NavigationMobile } from '@/features/navigation/navigation-mobile'
import { Locale } from '@/setup/locales'
import { UrlConfig, usePathname } from '@/setup/routing'
import { ExternalDeploymentUrl } from '@/setup/urls/external-deployment-urls'
import { compact } from '@/utils/functions/compact'

export const Navigation = () => {
    const locale = useLocale()

    const t = useTranslations()

    const baseItems = useMemo((): Record<string, Omit<INavigationDesktopItemProps, 'isActive'>> => {
        return {
            home: {
                linkParams: { pattern: '/', params: undefined },
                label: t('navigation.home'),
            },

            restaurants: {
                linkParams: { pattern: '/restaurants', params: undefined },
                label: t('navigation.restaurants'),
            },

            pricing: {
                linkParams: { pattern: '/pricing', params: undefined },
                label: t('navigation.pricing'),
            },

            faq: {
                linkParams: { pattern: '/faq', params: undefined },
                label: t('navigation.faq'),
            },

            // The jobs item is currently hidden, it is still linked in the footer
            // jobs: {
            //     linkParams: { pattern: '/jobs', params: undefined },
            //     label: t('navigation.jobs'),
            // },

            // The blog item is currently hidden
            // blog: {
            //     linkParams: { pattern: '/blog', params: undefined },
            //     label: t('navigation.blog'),
            // },

            becomeAPartner: {
                linkParams: {
                    pattern: '/become-a-partner',
                    params: undefined,
                },
                label: t('navigation.becomeAPartner'),
            },

            shop: {
                linkParams: {
                    externalUrl: ExternalDeploymentUrl.SHOP[locale as Locale],
                    target: '_blank',
                },
                label: t('navigation.shop'),
            },
        }
    }, [locale, t])

    const computedBaseItems = useMemo(() => {
        const isDutchLocale = locale === Locale.NL_NL || locale === Locale.EN_NL

        return Object.entries(baseItems).filter(([, item]) => {
            if (
                isDutchLocale &&
                item.linkParams &&
                'externalUrl' in item.linkParams &&
                item.linkParams.externalUrl === ExternalDeploymentUrl.SHOP[locale as Locale]
            ) {
                return false
            }

            return true
        })
    }, [baseItems, locale])

    const mobileItems = useMemo<Omit<INavigationDesktopItemProps, 'isActive'>[]>(
        () => computedBaseItems.map(([, item]) => item),
        [computedBaseItems]
    )

    const desktopItems = useMemo<Omit<INavigationDesktopItemProps, 'isActive'>[]>(
        () =>
            compact(
                computedBaseItems.map(([key, item]) => {
                    // On desktop we don't want to show the home item
                    const isHomeItem = key === 'home'

                    if (isHomeItem) return null

                    return item
                })
            ),
        [computedBaseItems]
    )

    const pathname = usePathname()

    const lpRoutePathname = UrlConfig[
        '/lp/monate-universal/[cityOrCountrySlug]/[a]/[[...customSlug]]'
    ].getUrl({ cityOrCountrySlug: null, customSlug: [] })

    const hideNaviagtion = pathname.startsWith(lpRoutePathname)

    if (hideNaviagtion) {
        return null
    }

    if (pathname.startsWith('/lp/cooperations')) {
        return null
    }

    const becomeAPartnerPlaceIdPage = UrlConfig['/become-a-partner/[placeIdSlug]'].getUrl({
        placeIdSlug: '',
    })

    if (pathname.startsWith(becomeAPartnerPlaceIdPage)) {
        return null
    }

    if (pathname === '/contact/mobile') {
        return null
    }

    if (pathname.startsWith('/sumup')) {
        return null
    }

    return (
        <Fragment>
            <NavigationMobile items={mobileItems} />
            <NavigationDesktop items={desktopItems} />
        </Fragment>
    )
}
